/**
 * Kalman Filter Service
 * Smooths GPS coordinates and reduces noise
 */

interface KalmanInput {
  lat: number;
  lng: number;
  accuracy: number;
}

interface KalmanOutput {
  lat: number;
  lng: number;
}

export class KalmanFilter {
  private variance: number;
  private minAccuracy: number;
  private lat: number | null = null;
  private lng: number | null = null;

  constructor(variance: number = -1, minAccuracy: number = 1) {
    this.variance = variance;
    this.minAccuracy = minAccuracy;
  }

  filter(input: KalmanInput): KalmanOutput {
    const accuracy = Math.max(input.accuracy, this.minAccuracy);

    // Check for outliers (large geographical jumps) and reset filter
    if (this.isOutlier(input)) {
      this.reset();
    }

    if (this.variance < 0) {
      // First measurement
      this.lat = input.lat;
      this.lng = input.lng;
      this.variance = accuracy * accuracy;
    } else {
      // ✅ OTIMIZADO: timeConstant = 0.8 (era 3 - muita suavização)
      const timeConstant = 0.8;
      this.variance += timeConstant * timeConstant;
      const kalmanGain = this.variance / (this.variance + accuracy * accuracy);

      // Update estimates
      this.lat = this.lat! + kalmanGain * (input.lat - this.lat!);
      this.lng = this.lng! + kalmanGain * (input.lng - this.lng!);
      this.variance = (1 - kalmanGain) * this.variance;
    }

    return {
      lat: this.lat!,
      lng: this.lng!
    };
  }

  isOutlier(input: KalmanInput): boolean {
    if (!this.lat || !this.lng) return false;
    
    // Calcular distância do último ponto
    const R = 6371e3;
    const φ1 = this.lat * Math.PI / 180;
    const φ2 = input.lat * Math.PI / 180;
    const Δφ = (input.lat - this.lat) * Math.PI / 180;
    const Δλ = (input.lng - this.lng) * Math.PI / 180;
    
    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const distance = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)) * R;
    
    // Se salto > 50m, é outlier
    return distance > 50;
  }

  reset() {
    this.variance = -1;
    this.lat = null;
    this.lng = null;
  }
}
